import React, { useState, useEffect } from 'react';
import { 
  calculateSpeakabilityScore, 
  getSpeakabilityTier, 
  SpeakabilityBreakdown as SpeakabilityBreakdownType 
} from '@/utils/speakabilityCalculator';
import SpeakabilityBreakdown from '@/components/SpeakabilityBreakdown';
import SpeakabilityGauge from '@/components/SpeakabilityGauge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import UserNav from '@/components/UserNav';
import { User } from '@/types/user';
import { AuthForms } from '@/components/AuthForms';

const SpeakabilityAnalyzerPage: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [results, setResults] = useState<SpeakabilityBreakdownType | null>(null);
  const [recentResults, setRecentResults] = useState<Array<{domain: string, score: number}>>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  
  const INITIAL_DISPLAY_COUNT = 5;

  // Load user data from localStorage/sessionStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user data', e);
      }
    }
  }, []);

  // Load recent results from localStorage
  useEffect(() => {
    const savedResults = localStorage.getItem('recentSpeakabilityResults');
    if (savedResults) {
      try {
        setRecentResults(JSON.parse(savedResults));
      } catch (e) {
        console.error('Error parsing stored results', e);
      }
    }
  }, []);

  const handleAnalyze = () => {
    if (!domain) {
      toast.warning('Please enter a domain name');
      return;
    }

    setIsAnalyzing(true);
    
    // Add a small delay to show processing (useful for UX feedback)
    setTimeout(() => {
      try {
        const breakdown = calculateSpeakabilityScore(domain);
        setResults(breakdown);
        
        // Save to recent results
        const newRecent = [
          { domain, score: breakdown.total },
          ...recentResults.filter(item => item.domain !== domain)
        ];
        
        setRecentResults(newRecent);
        localStorage.setItem('recentSpeakabilityResults', JSON.stringify(newRecent));
        
        setIsAnalyzing(false);
      } catch (error) {
        toast.error('Error analyzing domain');
        setIsAnalyzing(false);
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };
  
  const handleLogout = () => {
    // Clear stored user data
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    toast.success('Successfully logged out');
    setUser(null);
  };
  
  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    setShowAuthForm(false);
    toast.success('Successfully logged in');
  };

  return (
    <div className="min-h-screen py-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="absolute top-4 right-4 z-10">
        <UserNav 
          user={user} 
          onLogout={handleLogout} 
          onShowAuth={() => setShowAuthForm(true)}
        />
      </div>

      <div className="container mx-auto px-4">
        {showAuthForm ? (
          <div className="max-w-md mx-auto mt-16">
            <Button 
              variant="outline" 
              className="mb-4" 
              onClick={() => setShowAuthForm(false)}
            >
              Back to Analyzer
            </Button>
            <AuthForms onLogin={handleLogin} />
          </div>
        ) : (
          <>
            <header className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold mb-2">Speakability Analyzer</h1>
                <p className="text-gray-600">
                  Analyze how easy a domain name is to pronounce, remember, and communicate verbally.
                </p>
              </div>
            </header>

            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Enter domain name (e.g., example.com, dream-space, DreamSpace)"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1"
                  />
                  <Button onClick={handleAnalyze} disabled={isAnalyzing}>
                    {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                  </Button>
                </div>
                
                {recentResults.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Recent analyses:</p>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap gap-2">
                        {(showAllResults ? recentResults : recentResults.slice(0, INITIAL_DISPLAY_COUNT)).map((item) => (
                          <Button
                            key={item.domain}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setDomain(item.domain);
                              const breakdown = calculateSpeakabilityScore(item.domain);
                              setResults(breakdown);
                            }}
                            className="flex items-center gap-1.5"
                          >
                            <span>{item.domain}</span>
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">
                              {item.score}
                            </span>
                          </Button>
                        ))}
                      </div>
                      {recentResults.length > INITIAL_DISPLAY_COUNT && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowAllResults(!showAllResults)}
                          className="self-center mt-1 flex items-center gap-1"
                        >
                          {showAllResults ? (
                            <>Show Less <ChevronUp className="h-4 w-4" /></>
                          ) : (
                            <>See All <ChevronDown className="h-4 w-4" /></>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {results && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Speakability Score for "{domain}"</CardTitle>
                    <CardDescription className="flex items-center gap-1.5">
                      {getSpeakabilityTier(results.total).emoji} {getSpeakabilityTier(results.total).tier}
                      <span className="text-sm opacity-70"> - {results.total}/100</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col md:flex-row gap-8">
                    <div className="flex-1 flex justify-center">
                      <SpeakabilityGauge score={results.total} size="lg" />
                    </div>
                    <div className="flex-1">
                      <SpeakabilityBreakdown breakdown={results} />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col text-sm text-gray-500">
                    <p className="mb-2">
                      A higher speakability score indicates a name that is easier to pronounce,
                      communicate verbally, and remember after hearing it.
                    </p>
                    <p>
                      Our algorithm analyzes multi-word domains and uses <strong>pure phonetic analysis</strong> across four key dimensions:
                    </p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>
                        <strong>Fluency (0-40)</strong> - How smoothly the words flow when spoken
                      </li>
                      <li>
                        <strong>Familiarity (0-30)</strong> - How natural and familiar the phonetic patterns feel
                      </li>
                      <li>
                        <strong>Simplicity (0-30)</strong> - How clear and unambiguous the pronunciation is
                      </li>
                      <li>
                        <strong>Bonus (0-10)</strong> - Additional points for memorable characteristics
                      </li>
                    </ul>
                  </CardFooter>
                </Card>
              )}
              
              <div className="mt-8 text-sm text-gray-500">
                <h3 className="font-medium mb-2">What makes a name highly speakable?</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Balanced mix of consonants and vowels (35-65% vowels)</li>
                  <li>Moderate syllable length (1-3 syllables per word)</li>
                  <li>No difficult consonant clusters or tongue twisters</li>
                  <li>Clear pronunciation with no ambiguous sounds</li>
                  <li>Words that end with vowel sounds</li>
                  <li>Alternating consonant-vowel patterns</li>
                  <li>No confusing double letters</li>
                  <li>Short, clean domain names with 2-3 words maximum</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpeakabilityAnalyzerPage;
